// ============================================================================
// Board view — how ONE person arranges ONE board (item 818, 2026-10-08).
//
// Pure rules, no React, no network. The hook (use-board-view-prefs.ts) loads
// and saves a person's row in user_board_view_prefs; the kanbans and the Desk
// call arrangeCards() on each column with that row. Nothing here touches a
// card: a board arrangement is how work is looked at, never the work.
//
// Rules:
//   * Pinned cards sit at the top of their column, in the order they were
//     pinned, whatever the sort.
//   * "My order" places cards by manual_order; cards not listed follow, in the
//     order the board gave them.
//   * "Newest" / "Oldest" read created_at. "Start date soonest" reads the
//     card's start date, undated cards last — offered only where the board
//     has a start date.
//   * Drag-to-reorder (and Move up / Move down on a phone) only mean something
//     in "My order"; the controls say so rather than silently switching.
// ============================================================================

export type BoardSortMode = 'my_order' | 'start_soonest' | 'newest' | 'oldest';

export const BOARD_SORT_MODES: readonly BoardSortMode[] = [
  'my_order',
  'start_soonest',
  'newest',
  'oldest',
];

export const BOARD_SORT_LABELS: Record<BoardSortMode, string> = {
  my_order: 'My order',
  start_soonest: 'Start date soonest',
  newest: 'Newest first',
  oldest: 'Oldest first',
};

export interface BoardViewPrefs {
  sortMode: BoardSortMode;
  pinnedIds: string[];
  manualOrder: string[];
}

export const DEFAULT_BOARD_VIEW: BoardViewPrefs = {
  sortMode: 'my_order',
  pinnedIds: [],
  manualOrder: [],
};

/** What arranging needs to know about a card. */
export interface BoardCardFacts {
  id: string;
  /** ISO timestamp the card was created, or null. */
  createdAt: string | null;
  /** YYYY-MM-DD the work starts, or null. Only boards with a start date use it. */
  startDate?: string | null;
}

/** The board keys in use. One row per person per board. */
export type BoardKey = 'new-accounts' | 'hiring' | 'onboarding' | 'desk';

export function isBoardSortMode(v: unknown): v is BoardSortMode {
  return typeof v === 'string' && (BOARD_SORT_MODES as readonly string[]).includes(v);
}

function ts(v: string | null | undefined): number {
  if (!v) return Number.NaN;
  const t = Date.parse(v);
  return Number.isNaN(t) ? Number.NaN : t;
}

/**
 * Arrange one column's cards for one person. Stable: ties keep the order the
 * board gave them, so two people with no settings see the same board.
 */
export function arrangeCards<T>(
  rows: readonly T[],
  facts: (row: T) => BoardCardFacts,
  prefs: BoardViewPrefs,
): T[] {
  const indexed = rows.map((row, i) => ({ row, i, f: facts(row) }));
  const pinRank = new Map(prefs.pinnedIds.map((id, i) => [id, i] as const));
  const manualRank = new Map(prefs.manualOrder.map((id, i) => [id, i] as const));

  const byMode = (a: (typeof indexed)[number], b: (typeof indexed)[number]): number => {
    switch (prefs.sortMode) {
      case 'my_order': {
        const ra = manualRank.get(a.f.id);
        const rb = manualRank.get(b.f.id);
        if (ra !== undefined && rb !== undefined) return ra - rb;
        if (ra !== undefined) return -1;
        if (rb !== undefined) return 1;
        return a.i - b.i;
      }
      case 'newest':
      case 'oldest': {
        const ta = ts(a.f.createdAt);
        const tb = ts(b.f.createdAt);
        const aNan = Number.isNaN(ta);
        const bNan = Number.isNaN(tb);
        if (aNan && bNan) return a.i - b.i;
        if (aNan) return 1;
        if (bNan) return -1;
        if (ta === tb) return a.i - b.i;
        return prefs.sortMode === 'newest' ? tb - ta : ta - tb;
      }
      case 'start_soonest': {
        const ta = ts(a.f.startDate ?? null);
        const tb = ts(b.f.startDate ?? null);
        const aNan = Number.isNaN(ta);
        const bNan = Number.isNaN(tb);
        if (aNan && bNan) return a.i - b.i;
        if (aNan) return 1;
        if (bNan) return -1;
        if (ta === tb) return a.i - b.i;
        return ta - tb;
      }
    }
  };

  return indexed
    .sort((a, b) => {
      const pa = pinRank.get(a.f.id);
      const pb = pinRank.get(b.f.id);
      if (pa !== undefined && pb !== undefined) return pa - pb;
      if (pa !== undefined) return -1;
      if (pb !== undefined) return 1;
      return byMode(a, b);
    })
    .map((x) => x.row);
}

export function isPinned(prefs: BoardViewPrefs, id: string): boolean {
  return prefs.pinnedIds.includes(id);
}

/** Pin or unpin one card. Unpinning is a change, never a delete (decision 2A). */
export function togglePin(prefs: BoardViewPrefs, id: string): BoardViewPrefs {
  const pinned = prefs.pinnedIds.includes(id)
    ? prefs.pinnedIds.filter((x) => x !== id)
    : [...prefs.pinnedIds, id];
  return { ...prefs, pinnedIds: pinned };
}

/**
 * Record a new order for the ids of ONE column. Only this column's relative
 * order changes; ids from other columns keep their places after it. Cards
 * from different columns never compete, so the flat list is enough.
 */
export function setColumnOrder(prefs: BoardViewPrefs, columnIdsInOrder: string[]): BoardViewPrefs {
  const inColumn = new Set(columnIdsInOrder);
  const rest = prefs.manualOrder.filter((id) => !inColumn.has(id));
  return { ...prefs, manualOrder: [...columnIdsInOrder, ...rest] };
}

/**
 * Drag `activeId` and drop it on `overId` within one column whose cards are
 * currently shown as `shownIds` (already arranged). The active card takes the
 * dropped-on card's slot; pinned cards never move below an unpinned one.
 */
export function placeCard(
  prefs: BoardViewPrefs,
  shownIds: readonly string[],
  activeId: string,
  overId: string,
): BoardViewPrefs {
  if (activeId === overId) return prefs;
  const from = shownIds.indexOf(activeId);
  const to = shownIds.indexOf(overId);
  if (from < 0 || to < 0) return prefs;
  const next = [...shownIds];
  next.splice(from, 1);
  next.splice(to, 0, activeId);
  return setColumnOrder(prefs, next);
}

/** Move one card a step up or down inside its column (the phone's drag). */
export function moveCard(
  prefs: BoardViewPrefs,
  shownIds: readonly string[],
  id: string,
  direction: 'up' | 'down',
): BoardViewPrefs {
  const i = shownIds.indexOf(id);
  if (i < 0) return prefs;
  const j = direction === 'up' ? i - 1 : i + 1;
  if (j < 0 || j >= shownIds.length) return prefs;
  const pinned = new Set(prefs.pinnedIds);
  // A pinned card only trades places with another pinned card, and an
  // unpinned one never climbs into the pinned block.
  if (pinned.has(id) !== pinned.has(shownIds[j])) return prefs;
  const next = [...shownIds];
  [next[i], next[j]] = [next[j], next[i]];
  if (pinned.has(id)) {
    // reorder inside the pin list too, so the pin order (which wins) follows
    const pins = prefs.pinnedIds.filter((p) => next.includes(p)).sort((a, b) => next.indexOf(a) - next.indexOf(b));
    const others = prefs.pinnedIds.filter((p) => !next.includes(p));
    return setColumnOrder({ ...prefs, pinnedIds: [...pins, ...others] }, next);
  }
  return setColumnOrder(prefs, next);
}

/** Read a database row (text[] columns, free-text sort) into prefs, tolerating junk. */
export function prefsFromRow(row: {
  sort_mode?: string | null;
  pinned_ids?: string[] | null;
  manual_order?: string[] | null;
} | null | undefined): BoardViewPrefs {
  if (!row) return DEFAULT_BOARD_VIEW;
  return {
    sortMode: isBoardSortMode(row.sort_mode) ? row.sort_mode : 'my_order',
    pinnedIds: Array.isArray(row.pinned_ids) ? row.pinned_ids.filter((x) => typeof x === 'string') : [],
    manualOrder: Array.isArray(row.manual_order) ? row.manual_order.filter((x) => typeof x === 'string') : [],
  };
}
